"use client";

import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { handleError } from "@/lib/handle-error";
import { ActionErrorAlert } from "@/components/ui/action-error-alert";
import { useAppForm } from "@/components/form/use-app-form";
import {
  wasteAuditFormSchema,
  type WasteAuditFormValues,
} from "@/lib/validation/schemas";
import { wasteAuditFormDefaultValues } from "@/lib/validation/defaults";
import { WasteAuditSuccess } from "./waste-audit-success";
import { WasteAuditFormFields } from "./waste-audit-form-fields";
import { useSubmissionKey } from "@/lib/support/use-submission-key";
import {
  gatherClientDiagnostics,
  supportDiagnosticsFields,
} from "@/lib/feedback/client-diagnostics";

function buildWasteAuditMetadata(
  value: WasteAuditFormValues,
  pageUrl: string | undefined,
) {
  const packagingMaterialsStr = Array.isArray(value.packagingMaterials)
    ? value.packagingMaterials.join(", ")
    : "";

  const fullName = [value.firstName, value.lastName]
    .filter(Boolean)
    .join(" ");

  return {
    submitted_from: "waste_audit_form",
    page_path: pageUrl ?? null,
    firstName: value.firstName,
    lastName: value.lastName || null,
    name: fullName,
    email: value.email,
    phone: value.phone,
    companyName: value.company,
    service: value.service,
    packagingMaterials: packagingMaterialsStr || null,
    estimatedVolume: value.estimatedVolume,
  };
}

export default function WasteAuditForm() {
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitReportable, setSubmitReportable] = useState(false);
  const [submitErrorEventId, setSubmitErrorEventId] = useState<
    string | undefined
  >(undefined);

  const clearSubmitError = () => {
    setSubmitError(null);
    setSubmitReportable(false);
    setSubmitErrorEventId(undefined);
  };

  const createSupportRequest = useMutation(api.support.mutations.createRequest);
  const submissionKey = useSubmissionKey();

  const form = useAppForm({
    schema: wasteAuditFormSchema,
    defaultValues: wasteAuditFormDefaultValues,
  });

  async function onSubmit(value: WasteAuditFormValues) {
    if (value.botcheck) {
      console.warn("waste-audit-form.bot_detected", {
        botcheck: value.botcheck,
      });
      setIsSuccess(true);
      form.reset();
      return;
    }

    clearSubmitError();
    try {
      const diagnostics = gatherClientDiagnostics();
      await createSupportRequest({
        contactEmail: value.email,
        issueType: "waste_audit_request",
        message: value.additionalInfo || "No additional info provided",
        pagePath: diagnostics.url,
        metadata: buildWasteAuditMetadata(value, diagnostics.url),
        ...supportDiagnosticsFields(diagnostics),
        submissionKey: submissionKey.current(),
      });

      submissionKey.reset();

      setIsSuccess(true);
      form.reset();

      console.info("waste-audit-form.submitted", {
        companyName: value.company,
        service: value.service,
      });
    } catch (err) {
      const handled = handleError(err, { display: "silent" });
      if (handled.action === "ignore") return;
      setSubmitError(handled.message);
      setSubmitReportable(handled.reportable);
      setSubmitErrorEventId(handled.errorEventId);
    }
  }

  if (isSuccess) {
    return (
      <WasteAuditSuccess
        onReset={() => {
          setIsSuccess(false);
          form.reset();
        }}
      />
    );
  }

  return (
    <>
      {submitError && (
        <div className="mb-4">
          <ActionErrorAlert
            error={submitError}
            onDismiss={clearSubmitError}
            reportable={submitReportable}
            errorEventId={submitErrorEventId}
            reportContext={{ titlePrefix: "Waste audit submit failed" }}
          />
        </div>
      )}
      <WasteAuditFormFields form={form} onSubmit={onSubmit} />
    </>
  );
}
